import * as React from 'react';
import { BookingEntity } from 'Models/Entities';
import { addCustomStaffBookingNote, noteInfoDto } from 'Services/Api/_HumanWritten/NotesService';
import { useState } from 'react';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import { useBookingNotesModal } from 'Hooks/useBookingNotesModal';
import { getLowerCaseMessageByKey, getMessageByKey } from 'Stores/LanguageStore';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import alertToast from 'Util/ToastifyUtils';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { upperCaseFirst } from 'Util/StringUtils';
import moment from 'moment';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { noteType, smsDeliveryStatus } from 'Models/Enums';

export interface BookingNotesModalProps {
	booking: BookingEntity;
	customerCanViewNotes?: boolean;
}

export interface NoteProps {
	notes: noteInfoDto[];
}

export function BookingNotesModal({ booking, customerCanViewNotes }: BookingNotesModalProps) {
	const response = useBookingNotesModal(booking.id);
	const { isMobile } = useDeviceDetect();
	const [showNotes, setShowNotes] = useState<boolean>(!isMobile);
	const [noteInput, setNoteInput] = useState('');

	const createCustomStaffNote = async () => {
		if (booking != null && noteInput !== '') {
			const result = await addCustomStaffBookingNote({
				noteContent: noteInput,
				bookingId: booking.id,
			});

			if (result === false) {
				alertToast('Could not add new note', 'error');
			} else {
				setNoteInput('');
				response.refresh();
			}
		}
	};

	if (!response || response.type === 'error') {
		alertToast('Failed to fetch notes for booking.', 'error');
		return <></>;
	}

	const notes = (response.data as noteInfoDto[]) ?? [];

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		createCustomStaffNote();
	};

	return (
		<div className="notes-modal__container">
			<div
				key="notes-header"
				onKeyPress={() => {}}
				className="notes-modal__header"
				onClick={() => setShowNotes(!showNotes)}
			>
				<div className="left-container">
					<h6 className="title">Activity</h6>
					<h6 className="note-count">{notes?.length ?? 0}</h6>
				</div>
				<Button
					className={`show-hide-notes-panel-button ${
						showNotes ? 'icon-chevron-down' : 'icon-chevron-up'
					} icon-only`}
					colors={Colors.Secondary}
					display={Display.Text}
					onClick={() => setShowNotes(!showNotes)}
				/>
			</div>
			<If condition={showNotes}>
				<div className="past-notes__container">
					{renderNotes(notes, `${booking.user.firstName} ${booking.user.lastName}`)}
				</div>
				<div className="new-note__container">
					<form onSubmit={onSubmit}>
						<div className="new-note__input-wrapper custom-input">
							<textarea
								id="new_note-field"
								value={noteInput}
								onChange={e => setNoteInput(e.target.value)}
								autoComplete="off"
								placeholder="Write a note..."
								rows={2}
								className="new-note__textarea"
							/>
							<button
								type="submit"
								className={`new-note__send-btn ${noteInput ? 'active' : ''}`}
								disabled={!noteInput}
							>
								<Icon name="send" useMaterialIcons />
							</button>
						</div>
					</form>
					<p className="new-note__hint">
						{customerCanViewNotes
							? `Notes are visible to staff and ${booking.user?.firstName} ${booking.user?.lastName}.`
							: 'Notes are visible to staff only.'}
					</p>
				</div>
			</If>
		</div>
	);
}

function renderNotes(notes: noteInfoDto[], bookingUsername: string) {
	const listOfNotes: JSX.Element[] = [];
	if (isNullOrUndefined(notes)) {
		return <></>;
	}
	const uniqueDates = notes.filter(
		(noteInfo, index, arr) =>
			arr.findIndex(
				t => new Date(t.creationDateTime).toDateString() === new Date(noteInfo.creationDateTime).toDateString(),
			) === index,
	);

	uniqueDates.forEach(x => {
		notes.forEach(noteInfo => {
			// A "background note" is a one-line status event — checked in, status changed, payment taken —
			// rendered collapsed and lowercased with no body. It is chosen by matching substrings against
			// the note's text, which is fragile: `'check'` matches anywhere, so a real alteration note
			// mentioning the CHECK_IN_FORFEIT fee ("fee (check_in_forfeit): +$40.00") was swallowed into a
			// status line and lost its body.
			//
			// Note TYPE mostly settles what the text cannot: an ALTERATION note is a change record with a
			// breakdown worth rendering, while the status notes are COMMUNICATION or USER. The exception is
			// AlterationNote's own two outcome strings — "Started alteration and cancelled it" / "...and it
			// expired" — which are ALTERATION-typed but really are one-line status events, so they stay in
			// the background branch where they have always been.
			let backgroundNote = false;
			if (
				(noteInfo.content.includes('Started alteration') ||
					noteInfo.content.includes('check') ||
					noteInfo.content.includes('changed status') ||
					noteInfo.content.includes('invoiced') ||
					noteInfo.content.includes('awaiting') ||
					noteInfo.content.includes('set booking to') ||
					noteInfo.content.toLowerCase().includes('refund status') ||
					noteInfo.content.toLowerCase().includes('manually refunded') ||
					noteInfo.content.toLowerCase().includes('payment taken')) &&
				noteInfo.noteType !== 'STAFF' &&
				(noteInfo.noteType !== 'ALTERATION' || noteInfo.content.startsWith('Started alteration'))
			) {
				backgroundNote = true;
			}
			if (new Date(noteInfo.creationDateTime).toDateString() === new Date(x.creationDateTime).toDateString()) {
				if (backgroundNote) {
					listOfNotes.push(
						<div className="note__container" key={noteInfo.noteId}>
							<div className="note__header background_note">
								{(() => {
									if (noteInfo.content.toLowerCase().includes('refund status')) {
										return `${noteInfo.content} at ${formatStandardTime(noteInfo.creationDateTime)}`;
									}

									const content = noteInfo.content.toLowerCase();
									const author = noteInfo.createdBySystem ? 'System' : noteInfo.userName;
									const message = `${author} ${content} at ${formatStandardTime(noteInfo.creationDateTime)}`;
									const match = message.match(/to (.*?) at/);

									if (!match) {
										return message;
									}

									const [fullMatch, boldText] = match;
									const [before, after] = message.split(fullMatch);

									return (
										<p>
											{before}to <strong>{boldText}</strong> at{after.split('at')[1]}{' '}
											{formatStandardTime(noteInfo.creationDateTime)}
										</p>
									);
								})()}
							</div>
						</div>,
					);
				} else {
					listOfNotes.push(
						<div className="note__container" key={noteInfo.noteId}>
							<div className="note__header">
								<div className="note-author">
									<If condition={noteInfo.locationType === 'CHECK_IN'}>
										<div className="issue-icon grey-icon" />
									</If>
									<div>
										{noteInfo.createdBySystem ||
										(noteInfo.noteType === 'COMMUNICATION' && bookingUsername === noteInfo.userName)
											? 'System'
											: noteInfo.userName}
									</div>
								</div>
								<p className="note-created-time">{formatStandardTime(noteInfo.creationDateTime)}</p>
							</div>
							<div className={`note__content ${backgroundNote ? 'expire-reservation-note' : ''}`}>
								{convertContentToParagraphs(
									noteInfo.content,
									noteInfo.creationDateTime,
									noteInfo.noteType,
								)}
							</div>
							<If condition={!backgroundNote}>
								<div className="note__footer">
									<div className={`note__tag ${noteInfo.noteType.toLowerCase()}`}>
										{noteInfo.noteType === 'USER'
											? 'Customer'
											: upperCaseFirst(noteInfo.noteType.toLowerCase())}
									</div>
									<If condition={isNotNullOrUndefined(noteInfo.smsDeliveryStatus)}>
										<div className="note__delivery-status">
											{getSmsDeliveryStatus(noteInfo.smsDeliveryStatus)}
										</div>
									</If>
								</div>
							</If>
						</div>,
					);
				}
			}
		});
		// Add the date header for the above notes (sorry it's backwards)
		listOfNotes.push(
			<div className="date-line" key={new Date(x.creationDateTime).toDateString()}>
				<p className="date-header">{moment(x.creationDateTime).format('DD MMM YYYY').toUpperCase()}</p>
			</div>,
		);
	});

	return listOfNotes;
}

const convertContentToParagraphs = (content: string, createdDateTime: Date, typeOfNote: noteType) => {
	const isBefore2024 = new Date(createdDateTime) <= new Date('2023-03-07');
	if (
		content.includes('New Value') ||
		content.includes('Old Value') ||
		isBefore2024 ||
		(typeOfNote === 'STAFF' && !content.startsWith('Transaction fees changed:')) ||
		typeOfNote === 'COMMUNICATION'
	) {
		return [<p>{content}</p>];
	}
	const listOfStrings = content.split('\n');
	const listOfParagraphs: JSX.Element[] = [];
	let count = 0;
	let previousString = '';
	listOfStrings.forEach(paragraph => {
		if (paragraph !== '') {
			count++;
			const splitParagraph = paragraph.split(':');
			if (splitParagraph[0].includes('Booking moved from')) {
				listOfParagraphs.push(
					<>
						<p className="note-paragraph">Booking moved</p>
						<div key={count} className="note-paragraph">
							<p className="note-header">From</p>
							<p>{paragraph.split('Booking moved from')[1].split('to')[0]}</p>
						</div>
						<div key={count} className="note-paragraph">
							<p className="note-header">To</p>
							<p>{paragraph.split('to')[1]}</p>
						</div>
					</>,
				);
				return;
			}
			if (splitParagraph[0].includes('Ticket') && !splitParagraph[0].includes('Tickets')) {
				const newParas = paragraph.split('Ticket:');
				splitParagraph[0] = 'Ticket';
				// eslint-disable-next-line prefer-destructuring
				splitParagraph[1] = newParas[1];
			}
			if (splitParagraph[0].includes('Event date')) {
				const newParas = paragraph.split('Event date:');
				splitParagraph[0] = 'Event date';
				// eslint-disable-next-line prefer-destructuring
				splitParagraph[1] = newParas[1];
			}
			if (splitParagraph[0].includes('Voucher')) {
				const newSplit = splitParagraph[0].split('Voucher of ');
				splitParagraph[1] = `${newSplit[1] ?? ''}${splitParagraph[1] ?? ''}`;
				splitParagraph[0] = 'Voucher';
			}
			if (splitParagraph[0].includes('Fee')) {
				splitParagraph[1] = `${splitParagraph[1] ?? ''}${splitParagraph[2] ?? ''}`;
			}
			if (splitParagraph[0].includes('Cancelled booking') && !splitParagraph[0].includes('sent')) {
				listOfParagraphs.push(
					<div className="note-paragraph">
						<p key={count}>{splitParagraph[0]}</p>
						<p>___________________</p>
					</div>,
				);
			} else if (splitParagraph[0].includes('Created booking') || splitParagraph[0].includes('Updated booking')) {
				const newSplit = splitParagraph[0].split('(');
				listOfParagraphs.push(
					<div className="note-paragraph">
						<p key={count}>{newSplit[0]}</p>
						<p className="note-header">{newSplit[1]?.replace(')', '').toUpperCase()}</p>
						<p>___________________</p>
					</div>,
				);
			} else if (splitParagraph[0].includes('____________________')) {
				// We want to remove one of the lines so it fits on the width of the notes sidebar
				listOfParagraphs.push(
					<div className="note-paragraph">
						<p>___________________</p>
					</div>,
				);
			} else if (splitParagraph.length === 1) {
				listOfParagraphs.push(
					<div className="note-paragraph">
						<p key={count}>{splitParagraph[0]}</p>
					</div>,
				);
			} else if (
				previousString
					.toLowerCase()
					.includes(getLowerCaseMessageByKey('passenger-booking__additional-information__label')) ||
				previousString
					.toLowerCase()
					.includes(getLowerCaseMessageByKey('vehicle-tab__additional-information__label'))
			) {
				listOfParagraphs.push(
					<div className="note-paragraph">
						<p key={count}>{paragraph}</p>
					</div>,
				);
			} else {
				listOfParagraphs.push(
					<div className="note-paragraph">
						<p className="note-header">{splitParagraph[0]}</p>
						<p key={count}>{splitParagraph[1]}</p>
					</div>,
				);
			}
		}
		previousString = paragraph;
	});
	return listOfParagraphs;
};

function getSmsDeliveryStatus(deliveryStatus?: smsDeliveryStatus) {
	switch (deliveryStatus) {
		case 'DELIVERED':
			return '✓ Delivered';
		case 'UNDELIVERED':
			return '⚠ Undelivered';
		case 'EXPIRED':
			return '⚠ Expired';
		case 'REJECTED':
			return '⚠ Rejected';
		case 'DELETED':
			return '⚠ Deleted';
		case 'SENT':
			return '✓ Sent';
		case 'FAILED':
			return '⚠ Failed';
		case 'SENDING':
			return 'Sending…';
		case 'QUEUED':
			return 'Queued';
		default:
			return '';
	}
}
